import React, { useContext } from 'react';
import { Box, Typography } from '@mui/material';

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { StreakMeta } from '../data_types/types';
import { describeCurrentStreak } from '../data/player_insights';
import { DS, Overline, Panel, formatSigned, numericSx, trendColor } from '../shared/Dashboard';

const StreakRow: React.FC<{ label: string; description: string; streak: StreakMeta; color: string }> = ({ label, description, streak, color }) => {
    if (!streak) return null;
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.25,
            borderTop: `1px solid ${DS.border}`,
        }}>
            <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: '999px', background: color, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text }}>{label}</Typography>
                <Typography sx={{ fontSize: '12px', color: DS.textFaint, ...numericSx }}>
                    {description}
                    {streak.len > 0 && ` · #${streak.start_match_num}–#${streak.end_match_num}`}
                </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: DS.text, ...numericSx }}>
                    {streak.len} <Box component="span" sx={{ fontSize: '12px', color: DS.textMuted, fontWeight: 500 }}>rds</Box>
                </Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: trendColor(streak.points_gained), ...numericSx }}>
                    {formatSigned(streak.points_gained)} pts
                </Typography>
            </Box>
        </Box>
    );
};

const StreakData = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { summaryData } = context || {};
    const current: StreakMeta | undefined = summaryData.current_streak;
    const currentColor = current ? trendColor(current.points_gained >= 0 ? 1 : -1) : DS.neutral;

    return (
        <Panel title="Streaks" grid={{ xs: 12, md: 4 }} delay={0.1}>
            {current && current.len > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Overline color={currentColor}>Current · {describeCurrentStreak(current.points_gained, current.includesZero)}</Overline>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
                        <Typography sx={{ fontSize: '44px', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: DS.text, ...numericSx }}>
                            {current.len}
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: DS.textMuted }}>
                            round{current.len !== 1 ? 's' : ''}
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: currentColor, ml: 'auto', ...numericSx }}>
                            {formatSigned(current.points_gained)} pts
                        </Typography>
                    </Box>
                </Box>
            )}

            <StreakRow label="Longest climb" description="Rating up every round" streak={summaryData.longest_green_streak} color={DS.gain} />
            <StreakRow label="Longest run without a drop" description="Rating up or unchanged" streak={summaryData.longest_non_red_streak} color={DS.info} />
            <StreakRow label="Longest slide" description="Rating down every round" streak={summaryData.longest_red_streak} color={DS.loss} />
        </Panel>
    );
};

export default StreakData;
